import { useMemo } from "react";
import { Workspace } from "ts-interfaces";
import { CreditBalance, useCreditStore } from "../useCreditStore";
import { isDesignerRole } from "../team/teamRole";

export interface ActiveBalance {
  /**
   * The currently active workspace's balance. `null` when no balance has
   * loaded yet OR when the team workspace has no Stripe customer
   * configured (`hideBalance` is true in that case).
   */
  balance: CreditBalance | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Pre-flight check: is there enough in the active wallet for `required`
   * credits? Returns false if the wallet hasn't loaded or is suppressed.
   */
  hasEnough: (required: number) => boolean;
  workspace: Workspace | null;
  /**
   * True when the active workspace's balance should not be shown or spent.
   * Two cases: (a) the team's Stripe customer is not configured yet, or
   * (b) the user is a pure admin in the team (no designer seat) — admins
   * manage the team but cannot spend its credits, so the top-bar indicator
   * and pre-flight spend are suppressed. (Team management views read the
   * balance directly from the store, so they are unaffected.)
   */
  hideBalance: boolean;
}

/**
 * Reads the credit balance corresponding to the active workspace.
 *
 * Phase A.2a — additive shim on top of the existing useCreditStore. The store
 * still holds `balance` (personal) and `teamBalance` (team) as separate
 * fields; this hook selects the right one based on workspace.kind so that
 * downstream consumers stop branching on team membership themselves.
 *
 * Phase A.2b will fold the two store fields into one and make the
 * subscription itself workspace-driven; the API of this hook will not
 * change at that point — only its implementation.
 */
export const useActiveBalance = (
  workspace: Workspace | null | undefined,
): ActiveBalance => {
  const balance = useCreditStore((s) => s.balance);
  const teamBalance = useCreditStore((s) => s.teamBalance);
  const loadedFirmId = useCreditStore((s) => s.designFirmId);
  const hideTeamBalance = useCreditStore((s) => s.hideTeamBalance);
  const isLoading = useCreditStore((s) => s.isLoading);
  const error = useCreditStore((s) => s.error);

  return useMemo<ActiveBalance>(() => {
    if (!workspace) {
      return {
        balance: null,
        isLoading,
        error,
        hasEnough: () => false,
        workspace: null,
        hideBalance: false,
      };
    }
    const isTeam = workspace.kind === "team";
    // A pure admin (team workspace, no designer seat) can manage the team but
    // cannot spend its credits — treat as a hidden, non-spendable balance.
    const isAdminOnlyTeam =
      workspace.kind === "team" && !isDesignerRole(workspace.role);
    const hideBalance = isTeam && (hideTeamBalance || isAdminOnlyTeam);
    // The store holds ONE teamBalance, loaded for store.designFirmId. For a
    // multi-team user mid-switch it can still belong to the PREVIOUS team —
    // never show (or spend against) another team's wallet; treat as loading.
    const teamBalanceForThisWorkspace =
      workspace.kind === "team" && loadedFirmId === workspace.firmId
        ? teamBalance
        : null;
    const selected = isTeam ? teamBalanceForThisWorkspace : balance;
    const effective = hideBalance ? null : selected;
    return {
      balance: effective,
      isLoading,
      error,
      hasEnough: (required: number) => {
        if (!effective) return false;
        return effective.totalBalance >= required;
      },
      workspace,
      hideBalance,
    };
  }, [
    workspace,
    balance,
    teamBalance,
    loadedFirmId,
    hideTeamBalance,
    isLoading,
    error,
  ]);
};
